import { useEffect, useRef } from "react";

const ROWS = [
  "Beamer",
  "Full Toss",
  "Yorker",
  "Full",
  "Length ball",
  "Back of a length",
  "Short",
  "Half tracker",
];

const COLS = ["WOO", "OO", "OS", "MS", "LS", "DL", "WDL"];

const HEIGHTS = [
  "Head",
  "Shoulder",
  "Chest",
  "Waist",
  "Thigh",
  "Knee",
  "Shin",
  "Yorker",
];

export default function ZoneMatrixPicker({
  pitchZone,
  stumpZone,
  heightZone,
  batterStumpZone,
  batterHand = "RIGHT",
  activeMatrix = "pitch",
  onActiveMatrixChange,
  onChange,
}) {
  const switchTimerRef = useRef(null);
  const cols = batterHand === "LEFT" ? [...COLS].reverse() : COLS;
  const hasPitchSelection = Boolean(pitchZone && stumpZone);
  const hasHeightSelection = Boolean(heightZone && batterStumpZone);
  const activeToggleClass = "bg-[#41264E] text-white shadow-sm";
  const inactiveToggleClass = "text-text-secondary";

  useEffect(() => {
    return () => {
      if (switchTimerRef.current) {
        clearTimeout(switchTimerRef.current);
      }
    };
  }, []);

  const isPitchSelected = (row, col) => pitchZone === row && stumpZone === col;
  const isHeightSelected =
    (row, col) => heightZone === row && batterStumpZone === col;

  const handlePitchSelect = (row, col) => {
    if (isPitchSelected(row, col)) {
      if (switchTimerRef.current) {
        clearTimeout(switchTimerRef.current);
        switchTimerRef.current = null;
      }
      onChange(null, null, heightZone, batterStumpZone);
      onActiveMatrixChange?.("pitch");
      return;
    }
    onChange(row, col, heightZone, batterStumpZone);
    if (switchTimerRef.current) {
      clearTimeout(switchTimerRef.current);
    }
    switchTimerRef.current = setTimeout(() => {
      onActiveMatrixChange?.("height");
      switchTimerRef.current = null;
    }, 1000);
  };

  const handleHeightSelect = (row, col) => {
    if (isHeightSelected(row, col)) {
      onChange(pitchZone, stumpZone, null, null);
      return;
    }
    onChange(pitchZone, stumpZone, row, col);
  };

  const renderMatrix = ({
    rows,
    selectedRow,
    selectedCol,
    isSelected,
    onSelect,
  }) => (
    <div className="bg-bg-primary rounded-2xl py-3 px-3 border border-bg-secondary shadow-sm">
      <div
        className="grid gap-1 [--zm-label:56px] sm:[--zm-label:80px]"
        style={{
          gridTemplateColumns: `var(--zm-label) repeat(${cols.length}, minmax(36px, 1fr))`,
        }}
      >
        <div className="text-[6px] text-text-secondary py-1" />
        {cols.map((col) => {
          const colHighlighted = selectedCol === col;
          return (
            <div
              key={col}
              className={`text-[8px] text-center py-1 ${
                colHighlighted
                  ? "bg-green-600/50 rounded-sm font-semibold"
                  : "text-text-secondary"
              }`}
            >
              {col}
            </div>
          );
        })}
        {rows.map((row, ri) => (
          <div key={row} className="contents">
            <div
              className={`text-[10px] pr-2 py-1 ${
                selectedRow === row
                  ? "bg-green-600/50 pl-1 rounded-sm font-semibold"
                  : "text-text-secondary"
              }`}
            >
              {row}
            </div>
            {cols.map((col, ci) => {
              const selected = isSelected(row, col);
              // Single sequential cell number, left-to-right then top-to-
              // bottom of the rendered grid. 56 cells total (8 rows × 7
              // cols). Operator reads it visually and voices that
              // number — "23" → cell 23. Founder ask 2026-05-26.
              const cellNum = ri * cols.length + ci + 1;
              return (
                <button
                  key={`${row}-${col}`}
                  type="button"
                  onClick={() => onSelect(row, col)}
                  className={`relative h-11 sm:h-8 w-full border border-bg-secondary/80 rounded-sm ${
                    selected
                      ? "bg-green-600 border-green-600"
                      : "bg-bg-secondary/80 hover:bg-bg-secondary"
                  }`}
                  aria-pressed={selected}
                  title={`${row} / ${col} · voice: "${cellNum}"`}
                >
                  <span
                    className={`absolute inset-0 flex items-center justify-center font-mono text-[10px] pointer-events-none ${
                      selected ? "text-white" : "text-text-secondary/60"
                    }`}
                  >
                    {cellNum}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // Internal Pitch/Stump · Height/BatterStump sub-tabs removed — the parent
  // BallByBallPage now controls activeMatrix via the 3 top-level tabs
  // (Pitch map / Height impact / Wagon wheel), so the duplicate sub-tabs
  // here just added visual noise without adding capability.
  return (
    <div className="w-full space-y-3">
      {activeMatrix === "height"
        ? renderMatrix({
            rows: HEIGHTS,
            selectedRow: heightZone,
            selectedCol: batterStumpZone,
            isSelected: isHeightSelected,
            onSelect: handleHeightSelect,
          })
        : renderMatrix({
            rows: ROWS,
            selectedRow: pitchZone,
            selectedCol: stumpZone,
            isSelected: isPitchSelected,
            onSelect: handlePitchSelect,
          })}
    </div>
  );
}
